import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { adminApi } from "@/api/admin.api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { errorMessage } from "./first-round";
import { useAdminProduct } from "./hooks";
import { type Column, DataTable } from "./ui/DataTable";
import { PageHeader } from "./ui/PageHeader";

type PackChange = { price: number; quantity: number; isActive: boolean };

export function PacksManagement() {
  const { product } = useAdminProduct();
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: packs, isLoading, error } = useQuery({ queryKey: ["packs", product], queryFn: () => adminApi.listPacks(product) });
  const mutate = useMutation({
    mutationFn: (payload: { id: string } & PackChange) => adminApi.updatePack(payload.id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["packs", product] });
      toast({ title: "Pack saved", variant: "success" });
    },
    onError: (e) => toast({ title: "Couldn't save the pack", description: errorMessage(e), variant: "destructive" }),
  });

  const columns: Column<any>[] = [
    { key: "name", header: "Pack", sortValue: (p) => p.name, cell: (p) => <span className="font-medium">{p.name}</span> },
    { key: "feature", header: "Feature", sortValue: (p) => p.featureKey, cell: (p) => <code className="font-mono text-xs text-slate-600">{p.featureKey}</code> },
    {
      key: "edit",
      header: "Quantity · Price (USD) · On sale",
      cell: (p) => <PackEditor pack={p} saving={mutate.isPending} onSave={(data) => mutate.mutate({ id: p.id, ...data })} />,
    },
  ];

  return (
    <div className="max-w-5xl">
      <PageHeader title="Packs" description="One-off add-on packs: how many uses each pack adds, its price, and whether it's on sale." />
      <DataTable
        label="Packs"
        columns={columns}
        rows={packs}
        rowKey={(p) => p.id}
        isLoading={isLoading}
        error={error}
        emptyMessage="No packs for this product."
        pageSize={null}
      />
    </div>
  );
}

function PackEditor({ pack, saving, onSave }: { pack: any; saving: boolean; onSave: (data: PackChange) => void }) {
  const [q, setQ] = useState<string>(String(pack.quantity ?? 0));
  const [pr, setPr] = useState<string>(String(pack.price ?? 0));
  const [active, setActive] = useState<boolean>(!!pack.isActive);
  const valid = /^\d+$/.test(q.trim()) && /^\d+(\.\d{1,2})?$/.test(pr.trim());
  const dirty = Number(q) !== Number(pack.quantity ?? 0) || Number(pr) !== Number(pack.price ?? 0) || active !== !!pack.isActive;
  return (
    <div className="flex items-center gap-2">
      <Input className="h-8 w-20" inputMode="numeric" aria-label={`${pack.name} quantity`} value={q} onChange={(e) => setQ(e.target.value)} />
      <Input className="h-8 w-24" inputMode="decimal" aria-label={`${pack.name} price`} value={pr} onChange={(e) => setPr(e.target.value)} />
      <Switch aria-label={`${pack.name} on sale`} checked={active} onCheckedChange={setActive} />
      <Button size="sm" variant="outline" disabled={!dirty || !valid || saving} onClick={() => onSave({ price: Number(pr), quantity: Number(q), isActive: active })}>
        Save
      </Button>
      {!valid && <span className="text-xs text-red-600">Whole quantity, price like 9.99</span>}
    </div>
  );
}
